import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Currency } from '@/types';
import { DeclineAmountDisplay } from '@/modules/decline-ui/components/decline-amount-display';
import {
  DeclineChevronRightIcon,
  DeclineComparisonDivider,
} from '@/modules/decline-ui/components/decline-ui-graphics';

interface LimitComparisonCardProps {
  orderAmount: { amount: number; currency: string } | null;
  recommendedLimit: { amount: number; currency: string } | null;
  currency: string | Currency;
  language: string;
}

export function LimitComparisonCard({
  orderAmount,
  recommendedLimit,
  currency,
  language,
}: LimitComparisonCardProps): React.JSX.Element {
  const { t } = useTranslation();
  const isRtl = language.startsWith('ar');
  const orderAmountStrikeClass =
    'line-through decoration-solid [text-decoration-skip-ink:none] [text-underline-position:from-font]';
  const borderGradient = isRtl
    ? 'linear-gradient(270deg, rgba(255, 147, 153, 0.5) 0%, rgba(255, 210, 111, 0.5) 43.75%, rgba(85, 192, 145, 0.5) 73.57%)'
    : 'linear-gradient(90deg, rgba(255, 147, 153, 0.5) 0%, rgba(255, 210, 111, 0.5) 43.75%, rgba(85, 192, 145, 0.5) 73.57%)';

  return (
    <div
      className="w-full rounded-[20px] p-px"
      style={{
        background: borderGradient,
        boxShadow: '0px 2px 8px rgba(25, 40, 57, 0.09)',
      }}
    >
      <div className="rounded-[19px] bg-white px-[13px] py-[17px]">
        <div className="grid h-[76px] grid-cols-[1fr_28px_1fr] items-center pb-1">
          <div className="flex h-[56px] flex-col items-center justify-between text-center text-[#7E82AD]">
            <span className="text-[14px] font-medium leading-5 tracking-[-0.21px]">
              {t('decline_ui.comparison.order_amount')}
            </span>
            <span data-testid="decline-order-amount">
              <DeclineAmountDisplay
                amount={orderAmount}
                currency={currency}
                language={language}
                className="text-[18px] font-semibold leading-6 tracking-[-0.27px] text-[#7E82AD]"
                amountClassName={orderAmount ? orderAmountStrikeClass : undefined}
                currencyClassName={
                  orderAmount ? orderAmountStrikeClass : undefined
                }
              />
            </span>
          </div>

          <div className="relative flex h-[76px] w-[28px] items-center justify-center justify-self-center">
            <DeclineComparisonDivider />
            <div
              className="relative z-[1] size-7 rounded-[14px] bg-white"
              style={{
                boxShadow: '0px 2px 8px rgba(25, 40, 57, 0.09)',
              }}
            >
              <DeclineChevronRightIcon
                direction={isRtl ? 'right' : 'left'}
                className={
                  isRtl
?
                  'absolute top-0.5 -left-px'
                    : 'absolute top-0.5 left-[7px]'
                    
                }
              />
            </div>
          </div>

          <div className="flex flex-col items-center gap-2 text-center">
            <span className="text-[14px] font-medium leading-5 tracking-[-0.21px] text-[#7E82AD]">
              {t('decline_ui.comparison.recommended_limit')}
            </span>
            <span className="text-[20px] font-bold leading-7 tracking-[-0.3px] text-[#128351]">
              <DeclineAmountDisplay
                amount={recommendedLimit}
                currency={currency}
                language={language}
              />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
